import PropTypes from 'prop-types';
import React, { PureComponent, useContext } from 'react';
import window from 'window-or-global';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';
import { bind, unbind } from '@confluence-classic/confluence-event_DO_NOT_USE';

import { BLUEPRINT_WIZARD_LOAD_DIALOG_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	// WARN: disabling only to meet ESlint requirements during ESlint upgrade
	getExperienceTracker,
} from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { CreateDialogContext } from '@confluence/create-dialog-context';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal.ts';
import { waitForRequiredModule } from '@confluence/wrm/entry-points/waitForRequiredModule.ts';
import { requireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers.ts';

class PseudoWizardCreateDialogComponent extends PureComponent {
	static displayName = 'PseudoWizardDialog';
	static propTypes = {
		parentPageId: PropTypes.string,
		spaceKey: PropTypes.string,
		templateOriginSpaceKey: PropTypes.string,
		title: PropTypes.string,
		onClose: PropTypes.func,
		open: PropTypes.bool,
		blueprint: PropTypes.object,
		contentId: PropTypes.string,
		onContentUpdated: PropTypes.func,
		beforeUpdateRequest: PropTypes.func,
		onError: PropTypes.func,
	};

	static defaultProps = {
		onClose: () => {},
	};

	componentDidMount() {
		bind('remove.dialog', this._afterDialogClosed);
		this.forceReloadInNextOpen = true;
		this._setOpenState(this.props.open);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (this.props.spaceKey !== nextProps.spaceKey) {
			this.forceReloadInNextOpen = true;
		}
		if (nextProps.open) {
			nativeEditorComponentsPreloader();
		}
	}

	isOpening = false;

	forceReloadInNextOpen = true;

	_setOpenState = (openState) => (openState ? this._open() : this._close());

	/**
	 * Close the create dialog
	 * @private
	 */
	_close = (e) => {
		// Destroy the dialog
		if (typeof this._confluenceDialogClose === 'function') {
			// a defensive way to make sure legacy code should not break our React code due to unknown error.
			try {
				this._confluenceDialogClose();
			} catch (error) {
				if (process.env.NODE_ENV !== 'production') {
					console.error('Failing in attempt to close create page dialog: ', error);
				}
			}

			this._confluenceDialogClose = null;
		}
		unbind('remove.dialog', this._afterDialogClosed);
		this.props.onClose(e);
	};

	/**
	 * Triggered after dialog closed
	 * @private
	 */
	_afterDialogClosed = (e, { dialog }) => {
		if (dialog.id === 'create-dialog') {
			this._confluenceDialogClose = null;
			this._close(e);
		}
	};

	_isActive = () => !!window.document.querySelector('#create-dialog');

	_open = () => {
		if (!this._isActive() && !this.isOpening) {
			const {
				blueprint,
				spaceKey,
				templateOriginSpaceKey,
				parentPageId,
				title,
				contentId,
				onContentUpdated,
				beforeUpdateRequest,
				onError,
			} = this.props;

			if (blueprint) {
				getExperienceTracker().start({
					name: BLUEPRINT_WIZARD_LOAD_DIALOG_EXPERIENCE,
					timeout: ExperienceTimeout.DEFAULT,
				});

				this.isOpening = true;
				requireLegacyWRM(
					[
						'wrc!com.atlassian.confluence.plugins.dialog-wizard:dialog-wizard-resources',
						'wrc!create-content',
					],
					() => {
						// Although we've included 'wr!com.atlassian.plugins.atlassian-connect-plugin:dynamic-css-resources' here
						// The 'link-style-css' may have been executed somewhere else previously.
						// Requiring it again will not have effect. Here we are going to manually initialize it.
						waitForRequiredModule(
							{
								listener: 'PseudoWizardDialog',
								module: 'ac/confluence/style/link-style-css',
							},
							() => {
								const linkStyleCSS = window.require('ac/confluence/style/link-style-css');
								if (linkStyleCSS && typeof linkStyleCSS.addLinkTags === 'function') {
									linkStyleCSS.addLinkTags();
								}
							},
						);

						waitForGlobal(
							{
								listener: 'PseudoWizardDialog',
								globalProperty: 'Confluence.Blueprint.loadDialogAndOpenTemplate',
							},
							() => {
								const { contentBlueprintId, templateId, itemModuleCompleteKey } = blueprint;

								window.Confluence.Blueprint.loadDialogAndOpenTemplate({
									spaceKey,
									templateOriginSpaceKey,
									contentBlueprintId,
									templateId,
									initContext: {
										itemModuleCompleteKey,
										// no-parent-page is the contentId being set inside the drawer state when the user switch space
										// it is indicator that new page will be created at the top of the space
										// in that case we do not want to specify the parentPageId when sending to backend
										parentPageId: parentPageId === 'no-parent-page' ? null : parentPageId,
									},
									contentId,
									onContentUpdated,
									beforeUpdateRequest,
									onError,
									title,
								})
									.then(() => {
										this.isOpening = false;
										this.forceReloadInNextOpen = false;
										getExperienceTracker().succeed({
											name: BLUEPRINT_WIZARD_LOAD_DIALOG_EXPERIENCE,
										});
									})
									.fail((response) => {
										this.isOpening = false;
										this.forceReloadInNextOpen = false;
										getExperienceTracker().fail({
											name: BLUEPRINT_WIZARD_LOAD_DIALOG_EXPERIENCE,
											error: new Error(`Failed to open correct blueprint dialog ${response}`),
										});
									});
							},
						);
					},
					() => {
						getExperienceTracker().fail({
							name: BLUEPRINT_WIZARD_LOAD_DIALOG_EXPERIENCE,
							error: new Error('WRM is unavailable'),
						});
						if (process.env.NODE_ENV !== 'production') {
							console.error(
								'Pseudo wizard create dialog cannot be initialized when WRM is disabled.',
							);
						}
					},
				);
			}
		}
	};

	render() {
		return null;
	}
}

export const CreateDialogBlueprintWizardRenderer = () => {
	const {
		showBlueprintVariableWizard,
		parentPageId,
		spaceKey,
		templateOriginSpaceKey,
		title,
		onClose,
		blueprint,
		contentId,
		onContentUpdated,
		beforeUpdateRequest,
		onError,
	} = useContext(CreateDialogContext);

	if (showBlueprintVariableWizard) {
		return (
			<PseudoWizardCreateDialogComponent
				parentPageId={parentPageId}
				spaceKey={spaceKey}
				templateOriginSpaceKey={templateOriginSpaceKey}
				title={title}
				onClose={onClose}
				blueprint={blueprint}
				contentId={contentId}
				onContentUpdated={onContentUpdated}
				beforeUpdateRequest={beforeUpdateRequest}
				onError={onError}
				open
			/>
		);
	}
	return null;
};
